export * from './date'
export * from './oauth'
export * from './device'
export * from './assets'
export * from './route'
export * from './chat'
export * from './zodiac'

import { LANGUAGE_DISPLAY_MAP, ONLINE_STATUS } from '@/common/constants'
import { LIMIT_DESC_REGEXP } from '@/common/constants/regexp'
import { getAsset } from '@/utils/assets'
import { t } from '@/locales'

export const inBrowser = typeof window !== 'undefined'

/**
 * 检查是否为开发环境
 */
export const isDevelopment = import.meta.env.DEV || import.meta.env.VITE_ENV === 'development'

/**
 * 检查是否为测试环境
 */
export const isTest = !import.meta.env.DEV && import.meta.env.VITE_ENV === 'test'

/**
 * 检查是否为生产环境
 */
export const isProduction = !import.meta.env.DEV && import.meta.env.VITE_ENV === 'production'

/**
 * 检查是否为调试模式
 */
export const isDebugMode = isDevelopment || isTest

/**
 * 从 URL 中提取所有查询参数
 * @param {string} [url] - URL 字符串，默认使用 window.location.search
 * @returns {Object} 包含所有查询参数的对象
 */
export function extractAllUrlParams(url) {
  const params = new URLSearchParams(url || window.location.search)
  const allParams = {}

  // 将所有参数都提取到对象中
  for (const [key, value] of params.entries()) {
    allParams[key] = value
  }

  return allParams
}

/**
 * 为图片 URL 添加 CDN 裁剪参数
 * @param {string} url - 原始图片 URL
 * @param {Object} options - 裁剪选项
 * @param {number} [options.width] - 裁剪宽度
 * @param {number} [options.height] - 裁剪高度
 * @param {string} [options.mode='lfit'] - 裁剪模式 (lfit=等比缩放限制宽高, mfit=正方形裁剪, fill=固定宽高裁剪)
 * @param {number} [options.quality=90] - 图片质量 (1-100)
 * @returns {string} 添加了裁剪参数的图片 URL
 */
export function getResizedImageUrl(url, options = {}) {
  if (!url) return url

  const { width, height, mode = 'lfit', quality = 90 } = options

  // 如果没有指定宽高，直接返回原URL
  if (!width && !height) return url

  // 构建裁剪参数
  const params = []

  // 添加 resize 模式
  if (width && height) {
    params.push(`image/resize,m_${mode},w_${width},h_${height}`)
  } else if (width) {
    params.push(`image/resize,m_${mode},w_${width}`)
  } else if (height) {
    params.push(`image/resize,m_${mode},h_${height}`)
  }

  // 添加质量参数
  params.push(`quality,q_${quality}`)

  // 检查 URL 是否已经有查询参数
  const separator = url.includes('?') ? '&' : '?'
  const paramString = `x-oss-process=${params.join('/')}`

  return `${url}${separator}${paramString}`
}

/**
 * 获取统一前缀的存储键
 * @param {string} key - 键名
 * @returns {string} 存储键
 */
export function getStorageKey(key) {
  return `__WEBAPP_IWEE_${key}__`
}

/**
 * 将语言标识转换为本地语言显示名称
 * Convert language key to native language display name
 *
 * @param {string} languageKey - 语言标识（如 'English', 'Chinese'）
 * @returns {string} - 本地语言显示名称（如 'English', '中文'）
 */
export function getLanguageDisplayName(languageKey) {
  if (!languageKey) return ''

  // 如果在映射表中找到，返回本地语言名称
  if (LANGUAGE_DISPLAY_MAP[languageKey]) {
    return LANGUAGE_DISPLAY_MAP[languageKey]
  }

  // 如果没找到，返回原值
  return languageKey
}

/**
 * 将语言数组转换为本地语言显示名称数组
 * Convert language array to native language display names
 *
 * @param {string[]} languages - 语言标识数组
 * @returns {string[]} - 本地语言显示名称数组
 */
export function getLanguageDisplayNames(languages) {
  if (!Array.isArray(languages)) return []

  return languages
    .map(lang => getLanguageDisplayName(lang))
    .filter(Boolean) // 过滤掉空值
}

/**
 * 判断当前语言是否为本地语言
 * @param {string} languageKey
 * @returns
 */
export function isLocalLanguage(languageKey) {
  const browserLocale = navigator.language || navigator.userLanguage
  return browserLocale === languageKey || browserLocale.split('-')[0] === languageKey
}

/**
 * 获取在线状态信息
 * @param {number|null|undefined} status - 在线状态
 *   - null: 用户在特殊状态（如语音房），显示为 active
 *   - undefined: 未知状态，显示为 offline
 * @returns {Object} - 在线状态信息
 */
export function getOnlineStatusInfo(status) {
  // null 表示用户在特殊状态（如语音房），显示为 active
  if (status === null) {
    return {
      status: 'active',
      text: t('common.onlineStatus.active'),
    }
  }

  // undefined 表示未知状态，显示为 offline
  if (status === undefined) {
    return {
      status: 'offline',
      text: t('common.onlineStatus.offline'),
    }
  }

  switch (status) {
    case ONLINE_STATUS.ONLINE:
      return {
        status: 'online',
        text: t('common.onlineStatus.online'),
      }
    case ONLINE_STATUS.BUSY:
      return {
        status: 'busy',
        text: t('common.onlineStatus.busy'),
      }
    case ONLINE_STATUS.ACTIVE:
    case ONLINE_STATUS.PARTY_ROOM:
    case ONLINE_STATUS.SHOW_ROOM:
      // 活跃、语音房、秀场房间都显示为活跃
      return {
        status: 'active',
        text: t('common.onlineStatus.active'),
      }
    default:
      return {
        status: 'offline',
        text: t('common.onlineStatus.offline'),
      }
  }
}

/**
 * 获取视频通话价格展示信息
 * 根据用户资产（卡片）和主播状态计算是否显示免费标签和价格
 *
 * @param {Object} options - 配置选项
 * @param {Object} options.memberAsset - 用户资产信息
 * @param {number} options.memberAsset.private_call_1v1_card - 视频卡数量
 * @param {number} options.memberAsset.private_call_exp_card - 体验卡数量
 * @param {number} options.price - 视频通话价格（来自 anchorStatus.price 或 roomInfo.video_cost）
 * @param {Object} [options.anchorStatus] - 主播状态信息（可选，用于检查是否允许使用体验卡）
 * @param {number} [options.anchorStatus.free] - 主播是否允许使用体验卡（1: 允许, 0: 不允许）
 * @param {number} [options.defaultPrice] - 默认价格（默认值为 BOTTOM_LINE_PRICE）
 * @returns {Object} 价格展示信息
 * @returns {boolean} returns.showFree - 是否显示免费标签
 * @returns {boolean} returns.showPrice - 是否显示价格
 * @returns {number} returns.price - 价格值
 * @returns {string|null} returns.cardType - 卡片类型: 'video-card' | 'exp-card' | null
 */
export function getVideoCostInfo({ memberAsset, price, anchorStatus } = {}) {
  // 默认值
  const result = {
    showFree: false,      // 是否显示免费标签
    showPrice: false,     // 是否显示价格
    price: 0,             // 价格值
    cardType: null,       // 卡片类型: 'video-card' | 'exp-card'
  }

  // 1. 检查 private_call_1v1_card 无条件可用
  if (memberAsset?.private_call_1v1_card > 0) {
    result.showFree = true
    result.cardType = 'video-card'
    result.freeIcon = getAsset('@/assets/images/icons/video-card.svg')
    return result
  }

  // 2. 检查 private_call_exp_card
  const hasExpCard = memberAsset?.private_call_exp_card > 0
  if (hasExpCard) {
    // 如果提供了 anchorStatus，需要检查主播是否允许使用体验卡
    if (anchorStatus) {
      if (anchorStatus.free === 1) {
        result.showFree = true
        result.cardType = 'exp-card'
        result.freeIcon = getAsset('@/assets/images/icons/exp-card_video.svg')
        return result
      }
    } else {
      // 如果没有提供 anchorStatus（例如在视频通话等待弹窗中），直接显示免费
      result.showFree = true
      result.cardType = 'exp-card'
      result.freeIcon = getAsset('@/assets/images/icons/exp-card_video.svg')
      return result
    }
  }

  // 3. 其他情况，显示价格
  const finalPrice = price
  result.showPrice = true
  result.price = finalPrice

  return result
}
getVideoCostInfo.CARD_TYPE = {
  VIDEO_CARD: 'video-card',
  EXP_CARD: 'exp-card',
}

/**
 * 视频截图 - 通过阿里云 OSS 视频截图能力获取视频指定时间点的截图
 * 用于生成视频封面、预览图等，避免视频黑屏问题
 *
 * OSS 文档：https://help.aliyun.com/zh/oss/user-guide/video-snapshots
 *
 * @param {string} videoUrl - 视频 URL
 * @param {Object} options - 配置选项
 * @param {number} [options.t=1000] - 截图时间（毫秒），范围 [0, 视频时长]，默认 1000ms（第1秒）
 *                                     如果超过视频时长，则返回最后一帧；如果需要截取视频封面，则设置为 0
 * @param {number} [options.w=0] - 截图宽度（像素），默认 0 表示根据高度自动计算
 *                                  范围 [0, 视频宽度]
 * @param {number} [options.h=0] - 截图高度（像素），默认 0 表示根据宽度自动计算
 *                                  范围 [0, 视频高度]
 *                                  如果 w 和 h 都为 0，则输出为原视频宽高
 * @param {string} [options.f='jpg'] - 输出格式，默认 'jpg'
 *                                      可选值：'jpg' | 'png'
 * @param {string} [options.m='fast'] - 截图模式，默认
 *                                       - normal: 根据时间精确截图
 *                                       - 'fast' : 截取该时间点之前最近的一个关键帧
 * @param {string} [options.ar='auto'] - 自动旋转，默认 'auto'
 *                                        可选值：
 *                                        - 'auto': 根据视频信息自动旋转
 *                                        - 'h': 根据视频信息强制按高大于宽的模式旋转
 *                                        - 'w': 根据视频信息强制按宽大于高的模式旋转
 * @returns {string} - 带有截图参数的视频封面 URL，如果 videoUrl 无效则返回空字符串
 *
 * @example
 * // 基础使用 - 获取第1秒的截图
 * getVideoSnapshot('https://example.com/video.mp4')
 * // 返回：https://example.com/video.mp4?x-oss-process=video/snapshot,t_1000,f_jpg,w_0,h_0,m_fast,ar_auto
 *
 * // 获取第一帧（视频封面）
 * getVideoSnapshot('https://example.com/video.mp4', { t: 0 })
 *
 * // 获取第2秒，PNG格式
 * getVideoSnapshot('https://example.com/video.mp4', { t: 2000, f: 'png' })
 *
 * // 指定宽度为375px，高度自动计算（保持宽高比）
 * getVideoSnapshot('https://example.com/video.mp4', { w: 375 })
 *
 * // 指定宽高（可能会变形）
 * getVideoSnapshot('https://example.com/video.mp4', { w: 750, h: 1334 })
 */
export function getVideoSnapshot(videoUrl, options = {}) {
  if (!videoUrl || typeof videoUrl !== 'string') {
    return ''
  }
  const mergedOptions = {
    t: 0,              // 截图时间（毫秒），0 表示截图封面，其他时间表示截图指定时间点
    w: 0,              // 宽度（像素）
    h: 0,              // 高度（像素）
    f: 'jpg',          // 输出格式
    m: 'fast',         // 截图模式
    ar: 'auto',        // 自动旋转
    ...options,
  }
  mergedOptions.m === 'normal' && (delete mergedOptions.m)
  // 构建 OSS 视频截图参数
  const ossParams = Object.entries(mergedOptions).reduce((acc, [key, value]) => {
    if (value !== undefined && value !== null) {
      acc.push(`${key}_${value}`)
    }
    return acc
  }, []).join(',')

  // 判断 URL 是否已有查询参数
  const hasQueryParams = videoUrl.includes('?')
  const separator = hasQueryParams ? '&' : '?'

  // 拼接参数
  return `${videoUrl}${separator}x-oss-process=video/snapshot,${ossParams}`
}

/**
 * 从 limit_desc 解析出已购数量和限制数量
 * @param {string} limitDesc - 如 " (0/1)"
 * @returns {{ purchased: number, limit: number } | null} 解析结果，不匹配则返回 null
 */
export function parseLimitDesc(limitDesc) {
  if (typeof limitDesc !== 'string') return null
  const match = limitDesc.match(LIMIT_DESC_REGEXP)
  if (!match) return null
  return {
    purchased: Number(match[1]),
    limit: Number(match[2]),
  }
}
